import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { WHATSAPP_URL } from "@/components/WhatsAppButton";
import { allArticles } from "@/pages/Insights";
import NotFound from "@/pages/NotFound";

const renderInline = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      return (
        <Link key={i} to={m[2]}>
          {m[1]}
        </Link>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2>{block.text}</h2>;
    case "p":
      return <p>{renderInline(block.text)}</p>;
    case "list":
      return (
        <ul className="mb-8 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-base text-[#9DA3AE] font-light leading-relaxed">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A880] mt-2.5 shrink-0" />
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
    case "meta":
      return (
        <div className="mb-8 border-l-2 border-[rgba(197,168,128,0.35)] pl-4 space-y-1" data-testid="article-meta">
          {block.lines.map((l, i) => (
            <p key={i} className="text-[11px] text-[#6b7280] font-light !mb-0">{l}</p>
          ))}
        </div>
      );
    case "cta":
      return (
        <div className="my-10 bg-[#13151D] border border-[rgba(197,168,128,0.3)] rounded-2xl p-8 text-center grain relative overflow-hidden" data-testid="article-inline-cta">
          <h3 className="font-display text-2xl text-[#F4F3EF] mb-3 !mt-0">{block.title}</h3>
          <p className="text-sm text-[#9DA3AE] font-light max-w-lg mx-auto mb-6 !mb-6">{block.text}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={block.primary.to}
              data-testid="article-inline-cta-primary"
              className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-7 py-3.5 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300 no-underline"
            >
              {block.primary.label}
            </Link>
            {block.secondary && block.secondary.href === "whatsapp" && (
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-inline-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-7 py-3.5 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors duration-300 no-underline"
              >
                {block.secondary.label}
              </a>
            )}
          </div>
        </div>
      );
    case "table":
      return (
        <div className="overflow-x-auto rounded-xl border border-[rgba(197,168,128,0.2)] mb-8">
          <table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="bg-[#1A1D27]">
                {block.head.map((h) => (
                  <th key={h} className="px-5 py-3.5 text-[11px] uppercase tracking-[0.15em] text-[#C5A880] font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-t border-[rgba(197,168,128,0.1)]">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-5 py-3.5 text-sm ${ci === 0 ? "text-[#F4F3EF]" : "text-[#9DA3AE] font-light"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const article = allArticles.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const related = allArticles.filter((a) => a.slug !== slug).slice(0, 3);
  const articleImage = article.image.startsWith("http") ? article.image : `https://alcasa.co.uk${article.image}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    image: articleImage,
    datePublished: article.date,
    author: { "@type": "Organization", name: "ACRE — Al Casa Real Estate" },
    publisher: { "@type": "Organization", name: "ACRE — Al Casa Real Estate" },
  };

  return (
    <div data-testid={`article-page-${slug}`}>
      <SEO
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/insights/${slug}`}
        keywords={article.keywords}
        type="article"
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: article.title, path: `/insights/${slug}` },
        ]}
      />

      <section className="relative pt-36 pb-16 overflow-hidden" data-testid="article-hero">
        <img src={article.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/70 via-[#0B0C10]/85 to-[#0B0C10]" />
        <div className="relative max-w-3xl mx-auto px-6">
          <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9DA3AE] hover:text-[#E6CA65] transition-colors mb-8">
            <ArrowLeft size={14} /> ACRE Insights
          </Link>
          <p className="eyebrow mb-4">{article.category}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light text-[#F4F3EF] leading-[1.1]">{article.title}</h1>
          <p className="flex items-center gap-4 mt-6 text-xs text-[#6b7280]">
            <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {article.readTime}</span>
            <span>By the ACRE Advisory Team</span>
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.2)] mb-12">
              <img src={article.image} alt={article.title} className="w-full h-72 sm:h-96 object-cover" />
            </div>
          </Reveal>
          <article className="article-body" data-testid="article-body">
            {article.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </article>
          <p className="text-[11px] text-[#6b7280] font-light mt-12 pt-8 border-t border-[rgba(197,168,128,0.12)] leading-relaxed">
            This article is for general guidance only and should not be treated as financial, legal, tax or immigration
            advice. Figures and regulations are subject to change; seek independent professional advice for your
            circumstances.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="article-related">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl font-light text-[#F4F3EF] mb-10">Further reading</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`related-article-${a.slug}`} className="card-hover group bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-6 block">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#C5A880] mb-3">{a.category}</p>
                <h3 className="font-display text-xl text-[#F4F3EF] leading-snug group-hover:text-[#E6CA65] transition-colors duration-300">{a.title}</h3>
                <p className="text-[11px] text-[#6b7280] mt-3">{a.readTime}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand testid="article-cta" title="Questions this article didn't answer?" subtitle="Every buyer's position is different. A private consultation gives you answers specific to yours." />
    </div>
  );
};

export default ArticleDetail;
